import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { localizedAlternates, useLang } from "@/lib/i18n";
import api from "@/lib/apiClient";
import useSEO from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Clock, Info, Users } from "lucide-react";
import { HtmlContent } from "@/components/RichText";
import {
  instrumentCanonicalPath,
  instrumentCoverImage,
  instrumentRequestFailure,
  instrumentRobotsDirective,
  serverConfirmedInstrument,
} from "@/lib/instrumentSeo";

const INSTRUMENT_REQUEST_TIMEOUT_MS = 15000;
// Bump when the consent notice above the start button changes meaning.
export const START_CONSENT_VERSION = "start-notice-v1";

export default function InstrumentDetail() {
  const { slug } = useParams();
  const { t, lang, pick, lp } = useLang();
  const nav = useNavigate();
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [requestAttempt, setRequestAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErr(null);
    setInst(null);
    api.get(`/public/instruments/${slug}`, { timeout: INSTRUMENT_REQUEST_TIMEOUT_MS })
      .then((r) => {
        if (active) setInst(r.data);
      })
      .catch((error) => {
        if (active) {
          setErr(instrumentRequestFailure(error, {
            serverConfirmed: serverConfirmedInstrument(slug),
          }));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [slug, requestAttempt]);

  const instName = inst ? (pick(inst, "name") || slug) : slug;
  const instShort = inst ? (pick(inst, "short_description") || pick(inst, "description")) : "";
  const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160);
  const coverImg = instrumentCoverImage(inst, lang);
  const notFound = !loading && err === "not_found";
  const robots = instrumentRobotsDirective({ loading, error: err, instrument: inst });
  useSEO({
    title: inst ? `${instName} — Mindovia` : (lang === "id" ? "Tes — Mindovia" : "Test — Mindovia"),
    description: stripHtml(instShort) || (lang === "id"
      ? "Instrumen refleksi diri di Mindovia — anonim, singkat, tanpa akun."
      : "Self-reflection instrument on Mindovia — anonymous, short, no signup required."),
    url: instrumentCanonicalPath({ slug, loading, error: err, lang }),
    alternates: localizedAlternates(`/instruments/${slug}`),
    image: coverImg,
    robots,
  });

  const startSession = async () => {
    const { data } = await api.post("/public/sessions/start", {
      instrument_id: inst.id,
      accept_terms: true,
      consent_version: START_CONSENT_VERSION,
      lang,
    });
    localStorage.setItem(`se_session_${slug}`, data.id);
    nav(`/instruments/${slug}/demographics?session=${data.id}`);
  };

  if (loading) return <div className="py-20 text-center text-muted-foreground">…</div>;
  if (notFound) return <div className="py-20 text-center">Tes tidak ditemukan.</div>;
  if (err === "unavailable" || !inst) {
    return (
      <div className="py-20 text-center">
        <p>{lang === "id" ? "Tes ini sedang tidak tersedia." : "This test is unavailable right now."}</p>
        <Button className="mt-4" variant="outline" onClick={() => setRequestAttempt((attempt) => attempt + 1)}>
          {lang === "id" ? "Coba lagi" : "Try again"}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-14">
      <Link to={lp("/instruments")} className="text-sm text-muted-foreground hover:text-primary" data-testid="back-to-catalogue">← {lang === "id" ? "Kembali ke semua tes" : "Back to all tests"}</Link>
      <div className="mt-6 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 space-y-6 min-w-0">
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-terracotta-200 text-terracotta-700 bg-terracotta-50">
            {inst.development_status?.replaceAll("_", " ")}
          </Badge>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.1]" data-testid="instrument-title">{pick(inst, "name")}</h1>
          {/* On phones the cover sits right under the title, before the description. */}
          <div className="md:hidden reflect-ring rounded-3xl overflow-hidden aspect-[4/5]" data-testid="instrument-cover-mobile">
            <img src={coverImg} alt="" className="w-full h-full object-cover" />
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed"><HtmlContent html={pick(inst, "description")} /></p>

          <div className="grid grid-cols-3 gap-4 py-2">
            <Meta icon={Clock} label={t("detail.duration")} value={`${inst.duration_minutes} ${t("cat.min")}`} />
            <Meta icon={Info} label={t("detail.questions")} value={inst.questions?.length ?? "-"} />
            <Meta icon={Users} label={t("detail.age")} value={`${inst.target_age_min}–${inst.target_age_max}`} />
          </div>

          <div className="p-5 rounded-xl border border-terracotta-200 bg-terracotta-50/50 text-sm text-teal-800">
            <strong>{t("detail.disclaimer")}.</strong> {pick(inst, "disclaimer")}
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="start-consent-notice">
            {t("detail.consent_before").replace("{start}", t("detail.start"))}{" "}
            <Link to={lp("/legal/privacy")} className="underline hover:text-primary" data-testid="start-consent-privacy-link">
              {t("detail.consent_link")}
            </Link>.
          </p>

          <Button data-testid="start-instrument-btn" size="lg" onClick={startSession} className="rounded-full h-12 px-8 bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
            {t("detail.start")}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        <div className="md:col-span-5">
          <div className="hidden md:block reflect-ring rounded-3xl overflow-hidden aspect-[4/5]" data-testid="instrument-cover-desktop">
            <img src={coverImg} alt="" className="w-full h-full object-cover" />
          </div>
          {inst.dimensions?.length > 0 && (
            <div className="md:mt-6 space-y-3">
              <div className="text-sm font-medium">{lang === "id" ? "Aspek yang dilihat" : "Aspects covered"}</div>
              <div className="flex flex-wrap gap-2">
                {inst.dimensions.map((d) => (
                  <span key={d.id} className="px-3 py-1 text-xs rounded-full border border-border bg-cream-100">
                    {pick(d, "name")}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Meta = ({ icon: Icon, label, value }) => (
  <div className="p-4 rounded-xl border border-border bg-card">
    <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <div className="font-display text-xl mt-1">{value}</div>
  </div>
);
